import { useCallback, useEffect, useRef, useState } from 'react';
import { ErrorDeApi, ErrorDeVoz, mandarVoz } from './api';
import {
  AVISO_SEGUNDOS,
  comoReloj,
  formatoParaGrabar,
  MAXIMO_SEGUNDOS,
  mensajeDeFalloDeVoz,
} from './grabacion';

/** Lo que este navegador dice saber grabar. */
function admiteFormato(tipo: string): boolean {
  return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(tipo);
}

/**
 * Si en este navegador se puede ofrecer grabar.
 *
 * SE COMPRUEBA ANTES DE PINTAR EL BOTÓN, no al pulsarlo. Un botón que existe y
 * falla siempre es peor que uno que no está: quien lo pulsa cree que el fallo es
 * suyo. Hacen falta las tres cosas —el micrófono, el grabador y un formato que
 * el servidor entienda—, y basta que falte una para que no se ofrezca.
 *
 * `mediaDevices` solo existe en contextos seguros, así que esto es también lo
 * que hace que en una conexión sin cifrar la pantalla no prometa nada que no
 * pueda cumplir.
 */
function sePuedeGrabar(): boolean {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) return false;
  if (typeof MediaRecorder === 'undefined') return false;
  return formatoParaGrabar(admiteFormato) !== null;
}

/**
 * Contestar hablando, para las preguntas que lo admiten.
 *
 * ES UN AÑADIDO Y NUNCA UN SUSTITUTO. Debajo de esto siempre está la casilla de
 * escribir, y si algo de aquí no funciona —no hay micrófono, se denegó el
 * permiso, el navegador es viejo— lo único que pasa es que este bloque no
 * aparece. Nadie se queda sin poder contestar por eso.
 *
 * LO QUE SE ENTIENDE SE PONE EN LA CASILLA, NO SE GUARDA A ESCONDIDAS. Una
 * transcripción tiene erratas, y esta va a acabar en una historia clínica: la
 * persona tiene que verla y poder arreglarla. Por eso el texto baja al campo de
 * escribir y se le dice que lo revise.
 *
 * EL MICRÓFONO SE SUELTA SIEMPRE, pase lo que pase. Un navegador que se queda
 * con el punto rojo encendido después de grabar asusta, y con razón: parece que
 * sigue escuchando. Va en el `finally` de detener y también al desmontar.
 */

type Estado =
  | { fase: 'quieto' }
  | { fase: 'pidiendo' }
  | { fase: 'grabando'; segundos: number }
  | { fase: 'mandando' }
  | { fase: 'fallo'; mensaje: string };

export function Voz({
  preguntaId,
  hayEscrito,
  alTranscribir,
}: {
  preguntaId: string;
  /** Si ya hay algo escrito, para avisar de que grabar lo reemplaza. */
  hayEscrito: boolean;
  alTranscribir: (texto: string) => void;
}) {
  const [estado, setEstado] = useState<Estado>({ fase: 'quieto' });
  /** Se enciende al terminar bien: es el aviso de «léelo antes de seguir». */
  const [reciente, setReciente] = useState(false);

  const grabadora = useRef<MediaRecorder | null>(null);
  const pista = useRef<MediaStream | null>(null);
  const contador = useRef<ReturnType<typeof setInterval> | null>(null);

  /** Corta el micrófono y los temporizadores. Se puede llamar dos veces. */
  const soltarTodo = useCallback((): void => {
    if (contador.current) {
      clearInterval(contador.current);
      contador.current = null;
    }
    pista.current?.getTracks().forEach((t) => {
      t.stop();
    });
    pista.current = null;
    grabadora.current = null;
  }, []);

  // Al desmontar —cambiar de parte con la grabación en marcha, cerrar la
  // pestaña— el micrófono no puede quedarse abierto.
  useEffect(() => soltarTodo, [soltarTodo]);

  const detener = useCallback((): void => {
    grabadora.current?.stop();
  }, []);

  const empezar = useCallback((): void => {
    const formato = formatoParaGrabar(admiteFormato);
    if (!formato) {
      setEstado({ fase: 'fallo', mensaje: mensajeDeFalloDeVoz('formato') });
      return;
    }

    setReciente(false);
    setEstado({ fase: 'pidiendo' });

    void (async () => {
      let flujo: MediaStream;
      try {
        flujo = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        /**
         * Denegar el permiso y no tener micrófono llegan aquí igual, y da lo
         * mismo: en los dos casos lo que la persona puede hacer es escribirlo.
         * Distinguirlos solo serviría para escribir un mensaje más largo.
         */
        setEstado({ fase: 'fallo', mensaje: mensajeDeFalloDeVoz('sin_permiso') });
        return;
      }

      pista.current = flujo;
      const trozos: Blob[] = [];
      const registro = new MediaRecorder(flujo, { mimeType: formato });
      grabadora.current = registro;

      registro.addEventListener('dataavailable', (evento) => {
        if (evento.data.size > 0) trozos.push(evento.data);
      });

      registro.addEventListener('stop', () => {
        soltarTodo();
        const audio = new Blob(trozos, { type: formato });

        /**
         * Una grabación de medio segundo es un toque sin querer, no una
         * respuesta. Mandarla gastaría una llamada de pago para devolver «no se
         * entendió nada», que además se lee como si la persona hubiera hecho
         * algo mal.
         */
        if (audio.size < 1024) {
          setEstado({ fase: 'quieto' });
          return;
        }

        setEstado({ fase: 'mandando' });
        void (async () => {
          try {
            const texto = await mandarVoz(preguntaId, audio);
            alTranscribir(texto);
            setReciente(true);
            setEstado({ fase: 'quieto' });
          } catch (error) {
            if (error instanceof ErrorDeVoz) {
              setEstado({ fase: 'fallo', mensaje: mensajeDeFalloDeVoz(error.motivo) });
              return;
            }
            if (error instanceof ErrorDeApi && error.motivo === 'red') {
              setEstado({ fase: 'fallo', mensaje: mensajeDeFalloDeVoz('red') });
              return;
            }
            setEstado({ fase: 'fallo', mensaje: mensajeDeFalloDeVoz('no_se_pudo') });
          }
        })();
      });

      registro.start();
      setEstado({ fase: 'grabando', segundos: 0 });

      contador.current = setInterval(() => {
        setEstado((previo) => {
          if (previo.fase !== 'grabando') return previo;
          const segundos = previo.segundos + 1;
          // Se corta solo al llegar al tope: pasado ese punto, el servicio que
          // transcribe no puede con ello y se perdería la grabación entera.
          if (segundos >= MAXIMO_SEGUNDOS) detener();
          return { fase: 'grabando', segundos };
        });
      }, 1000);
    })();
  }, [preguntaId, alTranscribir, soltarTodo, detener]);

  if (!sePuedeGrabar()) return null;

  return (
    <div className="voz">
      {estado.fase === 'grabando' ? (
        <>
          <button type="button" className="voz__boton voz__boton--grabando" onClick={detener}>
            <span className="voz__pulso" aria-hidden="true" />
            Ya terminé
          </button>
          <p className="voz__reloj" role="timer">
            {comoReloj(estado.segundos)}
          </p>
          {estado.segundos >= AVISO_SEGUNDOS && (
            <p className="voz__nota" role="status">
              Queda poco para el máximo. Ve cerrando y ya lo revisas.
            </p>
          )}
        </>
      ) : (
        <button
          type="button"
          className="voz__boton"
          disabled={estado.fase === 'pidiendo' || estado.fase === 'mandando'}
          onClick={empezar}
        >
          <IconoMicrofono />
          {estado.fase === 'mandando'
            ? 'Pasando a texto…'
            : estado.fase === 'pidiendo'
              ? 'Un momento…'
              : hayEscrito
                ? 'Grabar otra vez'
                : 'Contestar hablando'}
        </button>
      )}

      {estado.fase === 'quieto' && hayEscrito && !reciente && (
        <p className="voz__nota">Si grabas otra vez, se reemplaza lo que hay escrito.</p>
      )}

      {estado.fase === 'mandando' && (
        <p className="voz__nota" role="status">
          Esto tarda unos segundos. No cierres la página.
        </p>
      )}

      {reciente && (
        <p className="voz__nota voz__nota--revisar" role="status">
          Esto es lo que se entendió. Léelo y corrige lo que haga falta antes de seguir.
        </p>
      )}

      {estado.fase === 'fallo' && (
        <p className="voz__nota voz__nota--problema" role="alert">
          {estado.mensaje}
        </p>
      )}
    </div>
  );
}

/** Un micrófono. Decorativo: lo que nombra el botón es su texto. */
function IconoMicrofono() {
  return (
    <svg className="voz__icono" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3" />
    </svg>
  );
}
